import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  queryKeys,
  type SignalDetailResponse,
  type SignalsResponse,
  type SignalView,
} from "./queries.js";
import type { SignalsSearch } from "./search-params.js";

/**
 * Acknowledgement is optimistic, and it lands on every cache that shows the
 * signal.
 *
 * An ack is idempotent and per user, so a failed one rolls back without
 * losing anything, and the feed stays responsive on a page an operator clicks
 * through quickly.
 *
 * Three caches can be showing the same row: the feed the reader is on, the
 * detail behind the modal, and any other filtered feed. Patching only the
 * first left the modal's button still saying "Mark as read" — the detail has
 * a five-minute staleTime, so nothing refetched it — and left a filtered feed
 * behind the modal showing an unread row.
 */
export function useAcknowledgeSignal(search: SignalsSearch) {
  const client = useQueryClient();
  // The same key the feed on screen is reading, so the optimistic patch lands
  // on the cache the reader is looking at.
  const key = queryKeys.signals(search);

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`/api/signals/${encodeURIComponent(id)}/ack`, {
        method: "POST",
        credentials: "same-origin",
      });
      if (!response.ok) throw new Error(`acknowledging ${id} returned ${response.status}`);
    },
    onMutate: async (id: string) => {
      const detailKey = queryKeys.signal(id);
      await Promise.all([
        client.cancelQueries({ queryKey: key }),
        client.cancelQueries({ queryKey: detailKey }),
      ]);
      const previous = client.getQueryData<SignalsResponse>(key);
      const previousDetail = client.getQueryData<SignalDetailResponse>(detailKey);
      const at = new Date().toISOString();
      if (previous) client.setQueryData<SignalsResponse>(key, markFeedRead(previous, id, at));
      if (previousDetail) {
        client.setQueryData<SignalDetailResponse>(detailKey, markDetailRead(previousDetail, at));
      }
      return { previous, previousDetail };
    },
    onError: (_error, id, context) => {
      if (context?.previous) client.setQueryData(key, context.previous);
      if (context?.previousDetail) {
        client.setQueryData(queryKeys.signal(id), context.previousDetail);
      }
    },
    onSettled: (_data, _error, id) => {
      // Marked stale, not refetched, and not awaited: `onMutate` has already
      // patched every cache that shows this row, so a refetch here downloads
      // the whole feed again and rebuilds the table's row model to arrive at
      // the rows already on screen. A reader clicking through the feed paid
      // that on every ack.
      //
      // Still every feed, not just this one: a filtered feed behind the modal
      // is reading a different key, and `onMutate` patched neither it nor a
      // detail nobody has opened. `refetchType: "none"` leaves them stale for
      // the next refetch that has a reader in front of it — a filter change or
      // a return to the feed mounts a fresh observer, and `useSignals` asks
      // for a window focus — which is when a divergence would matter.
      client.invalidateQueries({ queryKey: FEED_PREFIX, refetchType: "none" });
      client.invalidateQueries({ queryKey: queryKeys.signal(id), refetchType: "none" });
    },
  });
}

/** What `queryKeys.signals` starts every feed key with, whatever the filters are. */
const FEED_PREFIX = ["signals"] as const;

function markFeedRead(feed: SignalsResponse, id: string, at: string): SignalsResponse {
  return {
    ...feed,
    signals: feed.signals.map((signal) => (signal.id === id ? read(signal, at) : signal)),
    // `total` rides along untouched: reading a signal does not stop it
    // matching the filters, so the denominator must not move under the reader.
    unreadCount: Math.max(0, feed.unreadCount - 1),
  };
}

function markDetailRead(detail: SignalDetailResponse, at: string): SignalDetailResponse {
  return { ...detail, signal: read(detail.signal, at) };
}

function read(signal: SignalView, at: string): SignalView {
  return signal.acknowledgedAt === null ? { ...signal, acknowledgedAt: at } : signal;
}
